import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

interface GuaranteeSectionProps {
  headline: string;
  guarantee: string;
  details: string[];
  ctaText: string;
  ctaHref: string;
  riskStatement?: string;
  upsideStatement?: string;
}

// Icon components
const ShieldIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
  </svg>
);

const CheckCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ClockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const TrendingUpIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
  </svg>
);

export const GuaranteeSection: React.FC<GuaranteeSectionProps> = ({
  headline,
  guarantee,
  details,
  ctaText,
  ctaHref,
  riskStatement,
  upsideStatement,
}) => {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  return (
    <section ref={sectionRef} className="relative py-16 sm:py-24 md:py-32 bg-[#0a0a0a] overflow-hidden">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/30 via-transparent to-zinc-900/30" />

      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />
      </div>

      {/* Top border */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

      <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-800/50 border border-zinc-700/30 mb-6"
          >
            <ShieldIcon className="w-4 h-4 text-zinc-400" />
            <span className="text-zinc-400 text-sm">Our Guarantee</span>
          </motion.div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            {headline}
          </h2>

          <p className="text-lg text-zinc-400 leading-relaxed max-w-2xl mx-auto">
            {guarantee}
          </p>
        </motion.div>

        {/* Main content card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-4xl mx-auto mb-12"
        >
          <div className="relative bg-zinc-900/50 backdrop-blur-sm rounded-2xl border border-zinc-800/50 p-8 lg:p-10">
            {/* Decorative shield icon */}
            <div className="absolute -top-6 left-1/2 -translate-x-1/2">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <ShieldIcon className="w-6 h-6 text-emerald-400" />
              </div>
            </div>

            {/* Details list */}
            <div className="space-y-4 mt-4">
              {details.map((detail, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                  className="flex gap-4 p-4 rounded-xl bg-zinc-800/30 border border-zinc-700/20 hover:border-zinc-700/40 transition-colors"
                >
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/10 flex items-center justify-center mt-0.5">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-400" />
                  </div>
                  <p className="text-zinc-300 leading-relaxed">{detail}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Risk/Upside comparison */}
        {(riskStatement || upsideStatement) && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="max-w-4xl mx-auto mb-12"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              {riskStatement && (
                <div className="relative group">
                  <div className="relative bg-zinc-900/50 backdrop-blur-sm rounded-2xl border border-zinc-800/50 p-6 hover:border-zinc-700/50 transition-all duration-300">
                    {/* Icon */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-zinc-800/50 border border-zinc-700/30 flex items-center justify-center">
                        <ClockIcon className="w-5 h-5 text-zinc-500" />
                      </div>
                      <span className="text-zinc-500 text-xs font-medium uppercase tracking-wider">Your Risk</span>
                    </div>

                    <p className="text-xl text-white font-medium">{riskStatement}</p>

                    {/* Subtle indicator */}
                    <div className="mt-4 pt-4 border-t border-zinc-800/50">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-zinc-600"></div>
                        <span className="text-zinc-500 text-sm">Minimal commitment</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {upsideStatement && (
                <div className="relative group">
                  {/* Highlight badge */}
                  <div className="absolute -top-3 right-6 z-10">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                      <TrendingUpIcon className="w-3.5 h-3.5 text-emerald-400" />
                      <span className="text-emerald-400 text-xs font-medium">High Value</span>
                    </div>
                  </div>

                  <div className="relative h-full bg-zinc-900/50 backdrop-blur-sm rounded-2xl border border-emerald-500/20 p-6 hover:border-emerald-500/30 transition-all duration-300">
                    {/* Subtle glow */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                    <div className="relative">
                      {/* Icon */}
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                          <TrendingUpIcon className="w-5 h-5 text-emerald-400" />
                        </div>
                        <span className="text-emerald-400 text-xs font-medium uppercase tracking-wider">Your Upside</span>
                      </div>

                      <p className="text-xl text-white font-medium">{upsideStatement}</p>

                      {/* Subtle indicator */}
                      <div className="mt-4 pt-4 border-t border-emerald-500/10">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                          <span className="text-emerald-400/70 text-sm">Documented results</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Visual comparison line */}
            <div className="hidden sm:flex items-center justify-center mt-8">
              <div className="flex items-center gap-4 text-zinc-500 text-sm">
                <span>45 min</span>
                <div className="w-32 h-1 bg-zinc-800 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-zinc-600 to-emerald-500 rounded-full"
                    initial={{ width: 0 }}
                    animate={isInView ? { width: '100%' } : {}}
                    transition={{ duration: 1.5, delay: 0.8 }}
                  />
                </div>
                <span className="text-emerald-400">$150K+</span>
              </div>
            </div>
          </motion.div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="text-center"
        >
          <motion.a
            href={ctaHref}
            className="group inline-flex items-center gap-3 px-8 py-4 bg-white text-zinc-900 font-semibold rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-white/10"
            whileHover={{ y: -2, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {ctaText}
            <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default GuaranteeSection;
